import { useState } from 'react';
import { toast } from 'react-toastify';
import {
  fetchAvailableServiceTimes as fetchAvailableServiceTimesRequest,
  fetchCalendlyLink as fetchCalendlyLinkRequest,
} from 'src/services/client';
import {
  AvailabilityMode,
  IAvailableServiceTimeDay,
} from 'src/services/api/UserManagement/apiContracts';
import { sortTimeframesIntoDayOrder } from './schedulingHelpers';

export interface FetchCalendlyLinkResult {
  ok: boolean;
  schedulingUrl: string;
}

export interface UseServiceRequestSchedulingAPIReturn {
  availableServiceTimes: IAvailableServiceTimeDay[];
  isLoadingAvailability: boolean;
  fetchCalendlyLink: (email: string) => Promise<FetchCalendlyLinkResult>;
  fetchAvailableServiceTimes: (
    serviceProviderId: string,
    mode: AvailabilityMode
  ) => Promise<void>;
}

const TOAST_OPTIONS = {
  autoClose: 12000,
  position: 'top-center' as const,
};

const isOkResponse = (response: any): boolean =>
  response.ok || response.status === 200;

export const useServiceRequestSchedulingAPI =
  (): UseServiceRequestSchedulingAPIReturn => {
    const [availableServiceTimes, setAvailableServiceTimes] = useState<
      IAvailableServiceTimeDay[]
    >([]);
    const [isLoadingAvailability, setIsLoadingAvailability] =
      useState<boolean>(false);

    const fetchCalendlyLink = async (
      email: string
    ): Promise<FetchCalendlyLinkResult> => {
      try {
        const response: any = await fetchCalendlyLinkRequest(email);

        if (!isOkResponse(response)) {
          throw new Error(`Failed to fetch calendar link: ${response.status}`);
        }

        const payload: any = await response.json();

        // legacy/workaround: BE wraps the result in a second statusCode, and a
        // provider only counts as Calendly-enabled when both calendarLink and
        // calendlyAPIKey are present. No link just means manual scheduling.
        const isCalendlyEnabled: boolean = Boolean(
          payload?.statusCode === 200 &&
            payload?.data?.calendarLink &&
            payload?.data?.calendlyAPIKey
        );

        return {
          ok: true,
          schedulingUrl: isCalendlyEnabled ? payload.data.calendarLink : '',
        };
      } catch (err: any) {
        console.error(
          'useServiceRequestSchedulingAPI fetchCalendlyLink failed:',
          err
        );

        return { ok: false, schedulingUrl: '' };
      }
    };

    const fetchAvailableServiceTimes = async (
      serviceProviderId: string,
      mode: AvailabilityMode
    ): Promise<void> => {
      setIsLoadingAvailability(true);

      try {
        const response: any = await fetchAvailableServiceTimesRequest(
          serviceProviderId,
          mode
        );

        if (!isOkResponse(response)) {
          throw new Error(
            `Failed to fetch service time availability: ${response.status}`
          );
        }

        const payload: any = await response.json();
        const availableTimes: IAvailableServiceTimeDay[] = payload?.data ?? [];

        const orderedAvailableTimes: IAvailableServiceTimeDay[] =
          sortTimeframesIntoDayOrder(availableTimes);

        setAvailableServiceTimes(orderedAvailableTimes);
      } catch (err: any) {
        console.error(
          'useServiceRequestSchedulingAPI fetchAvailableServiceTimes failed:',
          err
        );
        toast.error(
          ' 🚫 Not able to fetch service time availability!',
          TOAST_OPTIONS
        );
      } finally {
        setIsLoadingAvailability(false);
      }
    };

    return {
      availableServiceTimes,
      isLoadingAvailability,
      fetchCalendlyLink,
      fetchAvailableServiceTimes,
    };
  };